import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';

const elements = {
  loginView: document.querySelector('#login-view'),
  viewerView: document.querySelector('#viewer-view'),
  loginForm: document.querySelector('#login-form'),
  password: document.querySelector('#password'),
  loginButton: document.querySelector('#login-button'),
  loginError: document.querySelector('#login-error'),
  babyName: document.querySelectorAll('[data-baby-name]'),
  videoHost: document.querySelector('#video-host'),
  audioHost: document.querySelector('#audio-host'),
  placeholder: document.querySelector('#video-placeholder'),
  statusDot: document.querySelector('#status-dot'),
  statusText: document.querySelector('#status-text'),
  soundButton: document.querySelector('#sound-button'),
  soundText: document.querySelector('#sound-text'),
  fullscreenButton: document.querySelector('#fullscreen-button'),
  reconnectButton: document.querySelector('#reconnect-button'),
  logoutButton: document.querySelector('#logout-button'),
  viewerCount: document.querySelector('#viewer-count'),
};

let room = null;
let connecting = false;
let muted = false;
let babyOnline = false;
let reconnectTimer = null;
let wakeLock = null;

bootstrap();

async function bootstrap() {
  bindEvents();
  try {
    const session = await api('/api/session');
    setBabyName(session.babyName || 'Baby');
    if (session.authenticated) {
      showViewer();
      await connectViewer();
    } else {
      showLogin();
    }
  } catch {
    showLogin();
    setLoginError('De webinterface is tijdelijk niet bereikbaar.');
  }
}

function bindEvents() {
  elements.loginForm.addEventListener('submit', handleLogin);
  elements.soundButton.addEventListener('click', handleSound);
  elements.fullscreenButton.addEventListener('click', toggleFullscreen);
  elements.reconnectButton.addEventListener('click', connectViewer);
  elements.logoutButton.addEventListener('click', logout);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', releaseWakeLock);
}

async function handleLogin(event) {
  event.preventDefault();
  if (connecting) {
    return;
  }

  const password = elements.password.value;
  if (!password) {
    setLoginError('Vul het wachtwoord in.');
    elements.password.focus();
    return;
  }

  setLoginBusy(true);
  setLoginError('');

  try {
    const result = await api('/api/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    setBabyName(result.babyName || 'Baby');
    elements.password.value = '';
    showViewer();
    await connectViewer();
  } catch (error) {
    setLoginError(error.message);
    elements.password.select();
  } finally {
    setLoginBusy(false);
  }
}

async function connectViewer() {
  if (connecting) {
    return;
  }

  connecting = true;
  clearReconnectTimer();
  elements.reconnectButton.hidden = true;
  setStatus('connecting', 'Verbinden…');
  clearMedia();

  if (room) {
    try {
      await room.disconnect();
    } catch {
      // De bestaande verbinding kan al gesloten zijn.
    }
    room = null;
  }

  try {
    const credentials = await api('/api/token', {
      method: 'POST',
      body: '{}',
    });

    const nextRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
      disconnectOnPageLeave: true,
      stopLocalTrackOnUnpublish: true,
    });

    configureRoom(nextRoom);
    room = nextRoom;

    await nextRoom.prepareConnection(credentials.server_url);
    await nextRoom.connect(
      credentials.server_url,
      credentials.participant_token,
      { autoSubscribe: true },
    );

    attachExistingTracks(nextRoom);
    updateParticipantCount();
    updateBabyStatus();
    await requestWakeLock();

    if (nextRoom.canPlaybackAudio) {
      setSoundButton(false, false);
    } else {
      setSoundButton(false, true);
    }
  } catch (error) {
    console.error(error);
    setStatus('offline', error.message || 'Verbinden mislukt');
    elements.reconnectButton.hidden = false;
  } finally {
    connecting = false;
  }
}

function configureRoom(targetRoom) {
  targetRoom
    .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
      attachTrack(track, participant.identity);
      updateBabyStatus();
    })
    .on(RoomEvent.TrackUnsubscribed, (track) => {
      detachTrack(track);
      updateBabyStatus();
    })
    .on(RoomEvent.ParticipantConnected, () => {
      updateParticipantCount();
      updateBabyStatus();
    })
    .on(RoomEvent.ParticipantDisconnected, () => {
      updateParticipantCount();
      updateBabyStatus();
    })
    .on(RoomEvent.ConnectionStateChanged, (state) => {
      if (state === ConnectionState.Reconnecting) {
        setStatus('connecting', 'Verbinding herstellen…');
      } else if (state === ConnectionState.Connected) {
        updateBabyStatus();
      }
    })
    .on(RoomEvent.Reconnected, () => {
      attachExistingTracks(targetRoom);
      updateBabyStatus();
    })
    .on(RoomEvent.Disconnected, () => {
      babyOnline = false;
      setStatus('offline', 'Verbinding verbroken');
      elements.reconnectButton.hidden = false;
      scheduleReconnect();
    })
    .on(RoomEvent.AudioPlaybackStatusChanged, () => {
      const blocked = !targetRoom.canPlaybackAudio;
      setSoundButton(muted, blocked);
    });
}

function attachExistingTracks(targetRoom) {
  for (const participant of targetRoom.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.track) {
        attachTrack(publication.track, participant.identity);
      }
    }
  }
}

function attachTrack(track, participantIdentity) {
  const element = track.attach();
  element.dataset.trackSid = track.sid || '';
  element.dataset.participantIdentity = participantIdentity;
  element.autoplay = true;
  element.playsInline = true;

  if (track.kind === Track.Kind.Video) {
    element.className = 'live-video';
    elements.videoHost.replaceChildren(element, elements.placeholder);
    elements.placeholder.hidden = true;
    babyOnline = true;
  } else if (track.kind === Track.Kind.Audio) {
    element.muted = muted;
    elements.audioHost.appendChild(element);
    babyOnline = true;
  }
}

function detachTrack(track) {
  for (const element of track.detach()) {
    element.remove();
  }
  if (!elements.videoHost.querySelector('video')) {
    elements.placeholder.hidden = false;
  }
}

function clearMedia() {
  for (const media of document.querySelectorAll('video, audio')) {
    media.remove();
  }
  elements.placeholder.hidden = false;
  babyOnline = false;
  updateParticipantCount();
}

function updateBabyStatus() {
  const hasVideo = Boolean(elements.videoHost.querySelector('video'));
  const hasAudio = Boolean(elements.audioHost.querySelector('audio'));
  babyOnline = hasVideo || hasAudio;

  if (room?.state === ConnectionState.Reconnecting) {
    setStatus('connecting', 'Verbinding herstellen…');
  } else if (babyOnline) {
    setStatus('live', 'Babyfoon is live');
  } else if (room?.state === ConnectionState.Connected) {
    setStatus('waiting', 'Wachten op de babyfoon');
  }
}

function updateParticipantCount() {
  if (!room) {
    elements.viewerCount.textContent = '0';
    return;
  }

  const viewers = [...room.remoteParticipants.values()].filter(
    (participant) => participant.identity.startsWith('viewer-'),
  );
  elements.viewerCount.textContent = String(viewers.length + 1);
}

async function handleSound() {
  if (!room) {
    return;
  }

  if (!room.canPlaybackAudio) {
    try {
      await room.startAudio();
    } catch (error) {
      console.error(error);
      setStatus('offline', 'Safari blokkeert het geluid');
      return;
    }
  }

  muted = !muted;
  for (const audio of elements.audioHost.querySelectorAll('audio')) {
    audio.muted = muted;
  }
  setSoundButton(muted, false);
}

function setSoundButton(isMuted, playbackBlocked) {
  elements.soundButton.hidden = false;
  if (playbackBlocked) {
    elements.soundButton.classList.add('attention');
    elements.soundText.textContent = 'Geluid inschakelen';
    elements.soundButton.querySelector('span.icon').textContent = '🔊';
    return;
  }

  elements.soundButton.classList.remove('attention');
  elements.soundText.textContent = isMuted ? 'Geluid aan' : 'Geluid uit';
  elements.soundButton.querySelector('span.icon').textContent = isMuted
    ? '🔇'
    : '🔊';
}

async function toggleFullscreen() {
  const target = document.querySelector('#viewer-shell');
  try {
    if (!document.fullscreenElement) {
      await target.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  } catch {
    // iOS Safari ondersteunt fullscreen niet op ieder element.
  }
}

async function logout() {
  clearReconnectTimer();
  if (room) {
    await room.disconnect().catch(() => {});
    room = null;
  }
  await api('/api/logout', { method: 'POST', body: '{}' }).catch(() => {});
  clearMedia();
  releaseWakeLock();
  showLogin();
  elements.password.focus();
}

function showLogin() {
  elements.loginView.hidden = false;
  elements.viewerView.hidden = true;
}

function showViewer() {
  elements.loginView.hidden = true;
  elements.viewerView.hidden = false;
}

function setBabyName(name) {
  for (const element of elements.babyName) {
    element.textContent = name;
  }
}

function setLoginBusy(busy) {
  elements.loginButton.disabled = busy;
  elements.password.disabled = busy;
  elements.loginButton.textContent = busy
    ? 'Verbinden…'
    : 'Kijken en luisteren';
}

function setLoginError(message) {
  elements.loginError.textContent = message;
  elements.loginError.hidden = !message;
}

function setStatus(kind, text) {
  elements.statusDot.dataset.status = kind;
  elements.statusText.textContent = text;
}

function scheduleReconnect() {
  clearReconnectTimer();
  reconnectTimer = window.setTimeout(connectViewer, 5000);
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

async function requestWakeLock() {
  if (!('wakeLock' in navigator) || document.visibilityState !== 'visible') {
    return;
  }
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    wakeLock.addEventListener('release', () => {
      wakeLock = null;
    });
  } catch {
    // Niet alle Safari-versies staan een wake lock toe.
  }
}

function releaseWakeLock() {
  wakeLock?.release().catch(() => {});
  wakeLock = null;
}

async function handleVisibilityChange() {
  if (document.visibilityState === 'visible' && room) {
    await requestWakeLock();
  }
}

async function api(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    // Lege of niet-JSON response.
  }

  if (!response.ok) {
    if (response.status === 401 && url !== '/api/login') {
      showLogin();
    }
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}
